import { Component, h } from 'preact'
import { connectStateResults } from 'react-instantsearch-dom'
import _ from 'underscore'
import Phrase from '../../libs/translationsUtils/phrase'
import tracker from '../../components/tracker/publish'
import { t } from '../../libs/translationsUtils/translations'
import {
  SearchResults,
  AllSearchResults,
  isZendeskResult,
  ZendeskHit,
  isCommunityResult,
  Hit,
  isSalesforceResult, SalesforceHit
} from '../algolia/components'

import CommunitySearchResults from './CommunitySearchResults'
import ZendeskSearchResults from './ZendeskSearchResults'
import SalesforceSearchResults from './SalesforceSearchResults'

const eventTracker = () => {
  tracker.trackEvent('userevent', {
    type: 'Topic Initiated',
    trigger: 'widget',
    source: 'search',
    user_role: window.inSidedData.user.role,
    button_label: t('Forum', 'main.navigation.add_topic')
  })
}

interface State {
}

interface Props {
  isGuest: boolean,
  isFederatedSearch: boolean,
  isFederatedSalesforceSearch: boolean,
  enableLoader: boolean,
  attachmentCdn: string,
  defaultCdn: string,
  communityId: string,
  pageCount: number,
  results: Object,
  loaderCallback: (enableLoader: boolean) => void,
  loadNextPage: () => void,
  searchResults: SearchResults,
  allSearchResults: AllSearchResults,
  totalTopicsAmount: (amount: number) => void
}

class SearchResultsContainer extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
    }

    this.getResultsToDisplay = this.getResultsToDisplay.bind(this)
    this.getZendeskHits = this.getZendeskHits.bind(this)
    this.getSalesforceHits = this.getSalesforceHits.bind(this)
    this.checkForScrollPosition = _.debounce(this.checkForScrollPosition.bind(this), 150)
  }

  getResultsToDisplay (props: Props) {
    const allSearchResults = props.allSearchResults
    if (!props.isFederatedSearch && !props.isFederatedSalesforceSearch) {
      return {
        communityResults: props.searchResults,
        zendeskResults: undefined,
        salesforceResults: undefined
      }
    }
    return {
      communityResults: allSearchResults && allSearchResults[this.props.communityId],
      zendeskResults: allSearchResults && allSearchResults[`${this.props.communityId}-zendesk`],
      salesforceResults: allSearchResults && allSearchResults[`${this.props.communityId}-salesforce`]
    }
  }

  getZendeskHits (): Array<ZendeskHit> {
    return this.getResultsToDisplay(this.props).zendeskResults?.hits.filter(isZendeskResult) || []
  }

  getSalesforceHits (): Array<SalesforceHit> {
    return this.getResultsToDisplay(this.props).salesforceResults?.hits.filter(isSalesforceResult) || []
  }

  getSalesforceNumberOfHits () {
    return this.getResultsToDisplay(this.props).salesforceResults?.nbHits || 0
  }

  getZendeskNumberOfHits () {
    return this.getResultsToDisplay(this.props).zendeskResults?.nbHits || 0
  }

  getCommunityHits (): Array<Hit> {
    return this.getResultsToDisplay(this.props).communityResults?.hits.filter(isCommunityResult) || []
  }

  getCommunityNumberOfHits () {
    return this.getResultsToDisplay(this.props).communityResults?.nbHits || 0
  }

  hasMorePages (results: SearchResults) {
    return this.props.pageCount < results.nbPages - 1
  }

  canLoadMore (): boolean {
    const results = this.getResultsToDisplay(this.props)
    const salesforceHasMore = !results.salesforceResults || this.hasMorePages(results.salesforceResults)
    const zendeskHasMore = !results.zendeskResults || this.hasMorePages(results.zendeskResults)
    const communityHasMore = !results.communityResults || this.hasMorePages(results.communityResults)

    if (!results.zendeskResults || !results.salesforceResults) return communityHasMore

    return zendeskHasMore || communityHasMore || salesforceHasMore
  }

  checkForScrollPosition () {
    if (!this.canLoadMore()) return

    if (this.props.enableLoader) return

    const searchResults = document.getElementsByClassName('thread-list-item')

    const itemToTriggerPagination = searchResults[searchResults.length - 3]
    if (!itemToTriggerPagination) return

    const itemToTriggerPaginationBoundaries = itemToTriggerPagination.getBoundingClientRect()

    if (window.innerHeight > itemToTriggerPaginationBoundaries.y) {
      this.props.loadNextPage()
    }
  }

  componentDidMount () {
    document.addEventListener('scroll', this.checkForScrollPosition)
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.checkForScrollPosition)
  }

  render ({ isGuest, searchResults, attachmentCdn, defaultCdn, enableLoader, pageCount, totalTopicsAmount }: Props) {
    let totalTopics = this.getCommunityNumberOfHits() + this.getZendeskNumberOfHits() + this.getSalesforceNumberOfHits()

    totalTopicsAmount(totalTopics)

    return (
      <div>
        <div className='list__items--topic-count'>{totalTopics} <Phrase p='Topic|Topics' m='Forum' count={{ value: totalTopics.toString() }} /></div>
        {
          totalTopics === 0 ? (
            <div
              className='thread-list-block thread-list-block-empty-status thread-list-block--meta forum-list-view-spacing forum-list-view-spacing--v-s'>
              <p className='p--full-width'>
                <Phrase p='searchresults.none.first' m='Forum' /> <strong>{ searchResults?.query }</strong>.
                <Phrase p='searchresults.none.last' m='Forum' />
              </p>

              <a
                href='/topic/new'
                className='btn--cta btn--new-topic qa-topic-new-button'
                onClick={() => eventTracker()}>
                <Phrase p='main.navigation.add_topic' m='Forum' />
              </a>
            </div>
          ) : (
            <div className='search-topic-list'>

              <CommunitySearchResults
                pageCount={pageCount}
                topicIds={this.getCommunityHits().map(h => h.id)}
                loaderCallback={this.props.loaderCallback}
                isGuest={isGuest}
                attachmentCdn={attachmentCdn}
              />

              <ZendeskSearchResults hits={this.getZendeskHits()} pageCount={pageCount} />

              <SalesforceSearchResults hits={this.getSalesforceHits()} pageCount={pageCount} />
            </div>
          )
        }
        {
          enableLoader && pageCount !== 0 ? (
            <div className='instant-search-loader_pagination'>
              <img
                src={`${defaultCdn}/images/spinner.gif`}
                alt='loading...' />
            </div>
          ) : null
        }
      </div>
    )
  }
}

const SearchResultsWithMetadata = connectStateResults(SearchResultsContainer)

export default SearchResultsWithMetadata
